import Component from "@glimmer/component";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { eq, not } from "ember-truth-helpers";
import { t, tKey } from "ember-intl";
import { task, restartableTask } from "ember-concurrency";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { shape, instanceOf } from "prop-types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ENV from "glesys-controlpanel/config/environment";
import Database from "glesys-controlpanel/models/database";
import Project from "glesys-controlpanel/models/project";
import Header from "glesys-controlpanel/components/g-modal/header";
import Body from "glesys-controlpanel/components/g-modal/body";
import Footer from "glesys-controlpanel/components/g-modal/footer";
import modalClose from "glesys-controlpanel/helpers/modal-close";
import GButton from "glesys-controlpanel/components/g-button";
import GInput from "glesys-controlpanel/components/g-input";
import Label from "glesys-controlpanel/components/label";
import Select, { Option } from "glesys-controlpanel/components/select";
import Cost from "glesys-controlpanel/components/cost";
import IconsLoadingSpinner from "glesys-controlpanel/components/icons/loading-spinner";
import DataLoader from "glesys-controlpanel/components/data-loader";
import TaskForm from "glesys-controlpanel/components/task-form";

dayjs.extend(utc);

@forbidExtraArgs
export default class ModalsDatabaseRestore extends Component {
  @service modal;
  @service http;
  @service router;
  @service abilities;
  @service store;

  @arg(
    shape({
      database: instanceOf(Database).isRequired,
      project: instanceOf(Project).isRequired,
    }).isRequired,
  )
  params;

  get database() {
    return this.params.database;
  }

  get project() {
    return this.params.project;
  }

  @tracked plans = [];
  @tracked selectedPlanKey = this.database.plan.key;
  @tracked selectedDataCenterKey = this.database.datacenterkey;
  @tracked name = `${this.database.name}-restored-${dayjs().format("YYYYMMDDHHmmss")}`;

  @tracked selectedTargetKey = "latest";
  @tracked exactTime = "";

  get window() {
    return this.database.pitr.restorewindow;
  }

  get earliest() {
    return dayjs.utc(this.window.earliest).add(59, "second").startOf("minute");
  }

  get latest() {
    return dayjs.utc(this.window.latest);
  }

  get timeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  get quickPicks() {
    let inWindow = (time) => !time.isBefore(this.earliest) && !time.isAfter(this.latest);

    return [
      { key: "latest", labelKey: tKey("component.modal.database-restore.point.latest"), time: null },
      { key: "1h", labelKey: tKey("component.modal.database-restore.point.1-hour"), time: dayjs().subtract(1, "hour") },
      {
        key: "6h",
        labelKey: tKey("component.modal.database-restore.point.6-hours"),
        time: dayjs().subtract(6, "hour"),
      },
      { key: "1d", labelKey: tKey("component.modal.database-restore.point.1-day"), time: dayjs().subtract(1, "day") },
      { key: "7d", labelKey: tKey("component.modal.database-restore.point.7-days"), time: dayjs().subtract(7, "day") },
      { key: "earliest", labelKey: tKey("component.modal.database-restore.point.earliest"), time: this.earliest },
    ].filter((pick) => pick.time === null || inWindow(pick.time));
  }

  get minDateTime() {
    return this.earliest.local().format("YYYY-MM-DDTHH:mm");
  }

  get maxDateTime() {
    return this.latest.local().format("YYYY-MM-DDTHH:mm");
  }

  get targetTime() {
    return this.exactTime ? dayjs(this.exactTime).utc().toISOString() : undefined;
  }

  get canSubmit() {
    return Boolean(this.name);
  }

  get dataCenters() {
    return this.store.peekAll("data-center").filter((dataCenter) => dataCenter.hasDatabaseSupport);
  }

  selectQuickPick = (pick) => {
    this.selectedTargetKey = pick.key;
    this.exactTime = pick.time ? pick.time.local().format("YYYY-MM-DDTHH:mm") : "";
  };

  onExactTimeChange = (event) => {
    this.exactTime = event.target.value;
    this.selectedTargetKey = this.exactTime ? null : "latest";
  };

  onNameChange = (event) => (this.name = event.target.value);

  selectPlan = (event) => {
    this.selectedPlanKey = event.target.value;
    this.estimatedCostTask.perform();
  };

  selectDataCenter = (event) => (this.selectedDataCenterKey = event.target.value);

  loadPlansTask = task(async () => {
    let response = await this.http.request({ url: `${ENV.apiBaseUri}/database/listplans` });
    this.plans = response.content.plans;

    if (this.plans.length && !this.plans.some((plan) => plan.key === this.selectedPlanKey)) {
      this.selectedPlanKey = this.plans[0].key;
    }

    this.estimatedCostTask.perform();
  });

  estimatedCostTask = restartableTask(async () => {
    if (this.abilities.cannot("access pricing for organization", this.project.organization)) {
      return null;
    }

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/database/estimatedcost?projectkey=${this.project.id}&plankey=${this.selectedPlanKey}`,
      method: "POST",
    });

    return response.content.billing;
  });

  restoreTask = task(async () => {
    let body = {
      sourceid: this.database.id,
      name: this.name,
      plankey: this.selectedPlanKey,
      datacenterkey: this.selectedDataCenterKey,
    };

    if (this.targetTime) {
      body.targettime = this.targetTime;
    }

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/database/restore`,
      method: "POST",
      body,
    });

    this.modal.close();
    this.router.transitionTo("databases.details", response.content.database.id);
  });

  <template>
    <Header>{{t "component.modal.database-restore.header"}}</Header>
    <Body>
      <p class="text-gray-500 pb-6">{{t "component.modal.database-restore.description"}}</p>

      <TaskForm id="database-restore-form" @task={{this.restoreTask}}>
        <Label>{{t "component.modal.database-restore.point-in-time-label"}}</Label>
        <div class="flex flex-wrap gap-2 mb-3">
          {{#each this.quickPicks as |pick|}}
            <GButton
              @color={{if (eq this.selectedTargetKey pick.key) "blue" "outline"}}
              @type="button"
              {{on "click" (fn this.selectQuickPick pick)}}
            >
              {{t pick.labelKey}}
            </GButton>
          {{/each}}
        </div>

        <GInput
          aria-label={{t "component.modal.database-restore.exact-time-label"}}
          data-test-database-restore-exact-time
          type="datetime-local"
          value={{this.exactTime}}
          min={{this.minDateTime}}
          max={{this.maxDateTime}}
          {{on "input" this.onExactTimeChange}}
        />
        <p class="text-gray-500 text-xs -mt-3 mb-4">
          {{t "component.modal.database-restore.timezone-hint" timezone=this.timeZone}}
        </p>

        <Label for="restore-name">{{t "label.name"}}</Label>
        <GInput
          id="restore-name"
          data-test-database-restore-name
          value={{this.name}}
          {{on "input" this.onNameChange}}
        />

        <DataLoader @load={{this.loadPlansTask}}>
          <Label for="restore-plan">{{t "label.plan"}}</Label>
          <Select
            class="mb-4 text-sm"
            id="restore-plan"
            aria-label={{t "label.plan"}}
            @value={{this.selectedPlanKey}}
            {{on "change" this.selectPlan}}
          >
            {{#each this.plans as |plan|}}
              <Option value={{plan.key}}>
                {{plan.cpucores}}
                CPU /
                {{plan.memoryingib}}
                GiB /
                {{plan.storageingib}}
                GiB
              </Option>
            {{/each}}
          </Select>
        </DataLoader>

        <Label for="restore-data-center">{{t "label.data-center"}}</Label>
        <Select
          class="text-sm"
          id="restore-data-center"
          aria-label={{t "label.data-center"}}
          data-test-database-restore-data-center
          @value={{this.selectedDataCenterKey}}
          {{on "change" this.selectDataCenter}}
        >
          {{#each this.dataCenters as |dataCenter|}}
            <Option value={{dataCenter.id}}>
              {{dataCenter.name}}
              ({{dataCenter.id}})
            </Option>
          {{/each}}
        </Select>
      </TaskForm>

      <div class="flex justify-end items-center mt-4 min-h-8">
        {{#if this.estimatedCostTask.isRunning}}
          <IconsLoadingSpinner class="size-6" />
        {{else if this.estimatedCostTask.last.value.estimated}}
          <Cost
            @price={{this.estimatedCostTask.last.value.estimated.price}}
            @discountedPrice={{this.estimatedCostTask.last.value.estimated.total}}
            @currency={{this.estimatedCostTask.last.value.currency}}
          />
        {{/if}}
      </div>
    </Body>
    <Footer>
      <GButton class="mr-2" data-test-cancel-button @color="gray" {{on "click" (modalClose)}}>
        {{t "button.close"}}
      </GButton>
      <GButton
        data-test-database-restore-submit
        @type="submit"
        @color="blue"
        @disabled={{not this.canSubmit}}
        @loading={{this.restoreTask.isRunning}}
        form="database-restore-form"
      >
        {{t "button.restore"}}
      </GButton>
    </Footer>
  </template>
}
